<template>
  <div v-if="customAccount" class="payout-list">
    <div class="d-flex justify-content-between mb-3">
      <h3 class="page-subtitle mb-2">Payout Details</h3>
      <div>
        <b-button
          class="edit-btn btn--xs"
          variant="outline-secondary"
          v-show="externalAccounts.length > 0"
          @click="isEditing = !isEditing"
          >{{ isEditing ? 'Cancel' : 'Edit' }}</b-button
        >
      </div>
    </div>

    <div v-if="!loading && externalAccounts.length === 0">
      {{ displayText }}
    </div>

    <div v-if="loading">
      <card-shimmer
        v-for="i in 3"
        :key="i"
        width="100%"
        height="3.75em"
        :rounded="true"
        class="mb-4"
      ></card-shimmer>
    </div>

    <div v-else class="form__section">
      <b-list-group>
        <b-list-group-item
          v-for="account in externalAccounts"
          :key="account.id"
          :class="
            account.default_for_currency && !isEditing
              ? 'list-group-item--default'
              : ''
          "
          @click="!isEditing ? setDefaultCard(account.id) : ''"
        >
          <div class="d-flex">
            <b-img class="mt-1" v-bind="mainProps" rounded alt="Logo"></b-img>
            <div class="ml-4">
              {{ account.bank_name }}
              <div>
                {{ account.routing_number }}
                <span class="ml-4">•••••&nbsp;&nbsp;{{ account.last4 }}</span>
                <div
                  class="remove-link"
                  v-show="account.default_for_currency && !isEditing"
                >
                  <b-link>
                    <b-icon scale="1.8" icon="check2"></b-icon>
                  </b-link>
                </div>
                <div
                  :class="[
                    'remove-link',
                    {
                      'remove-link--disabled': account.default_for_currency
                    }
                  ]"
                  @click="
                    !account.default_for_currency
                      ? onClickRemove(account.id)
                      : ''
                  "
                  v-show="isEditing"
                >
                  <b-link>Remove</b-link>
                </div>
              </div>
            </div>
          </div>
        </b-list-group-item>
      </b-list-group>
    </div>

    <b-modal
      v-model="confirmDelete"
      centered
      class="modal-confirmation text-center p-3"
      hide-footer
      static
    >
      <b-img
        slot="modal-header-close"
        :src="require('@/assets/icons/x-b.svg')"
        width="45"
        height="30"
      ></b-img>

      <div
        class="d-flex flex-column justify-content-between align-items-center text-center"
      >
        <h2>Delete bank account</h2>

        <b-img
          :src="require('@/assets/images/alert.svg')"
          width="150"
          height="150"
          class="modal-confirmation__icon"
        ></b-img>

        <p class="my-4">
          Are you sure you want to permanently delete this bank account?
        </p>
        <div class="modal-confirmation__footer">
          <b-button variant="outline-secondary" @click="confirmDelete = false"
            >Cancel</b-button
          >
          <b-button variant="danger" :disabled="loading" @click="onClickDelete">
            <b-spinner small v-if="loading"></b-spinner>&nbsp;Delete
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="show"
      title="Add Payout Detail"
      centered
      hide-footer
      size="md"
      static
      class="modal-page modal-form"
      @hidden="onModalHidden"
    >
      <b-img
        slot="modal-header-close"
        :src="require('@/assets/icons/x.svg')"
        width="40"
        height="24"
      ></b-img>
      <payout-form
        @on-success="show = false"
        @on-close="show = false"
        ref="payoutForm"
      />
    </b-modal>

    <div>
      <b-button
        type="submit"
        variant="primary"
        class="submit-btn"
        @click="onSubmit"
      >
        <b-spinner small v-if="loading"></b-spinner>
        {{ buttonText }}
      </b-button>
    </div>
  </div>
  <div v-else>
    <b-alert
      :show="errorMessage"
      variant="danger"
      class="account-alert alert__padding alert__border d-flex flex-row align-items-center"
    >
      <div>
        <b-img
          :src="require('@/assets/images/alert.svg')"
          :width="55"
          alt="Alert"
          class="mr-3"
        ></b-img>
      </div>
      <div>
        <p>
          <b>Sorry, we've encountered an error connecting to Stripe</b>
        </p>
        <p>{{ errorMessage }}</p>
      </div>
    </b-alert>
  </div>
</template>
<script>
/**
 * ==================================================================================
 * Payout Details
 * ==================================================================================
 **/

import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import PayoutForm from '@/components/forms/PayoutForm'
import CardShimmer from '@/components/shimmers/shimmer'

export default {
  components: {
    PayoutForm,
    CardShimmer
  },
  computed: {
    ...mapState({
      customAccount: (state) => state.account.customAccount,
      externalAccounts: (state) => state.account.list
    }),

    ...mapGetters({
      customAccountStatus: 'account/customAccountStatus'
    }),

    displayText() {
      if (this.customAccount.requirements.currently_due.length > 3) {
        return 'Connect with Stripe to add your basic information'
      } else if (!this.customAccountStatus) {
        return 'Connect with Stripe to upload identity verification requirements'
      } else {
        return 'Onboarding requirements have been submitted and verified'
      }
    },

    buttonText() {
      if (this.customAccount.requirements.currently_due.length > 3) {
        return ' Connect with Stripe'
      } else if (!this.customAccountStatus) {
        return ' Upload Documents'
      } else {
        return ' Add Bank Account'
      }
    }
  },

  data: function () {
    return {
      loading: false,
      show: false,
      isEditing: false,
      confirmDelete: false,
      current: 0,
      errorMessage: null,
      mainProps: {
        blank: true,
        blankColor: '#00CE7D',
        width: 40,
        height: 40
      }
    }
  },
  methods: {
    ...mapActions({
      getCustomAccount: 'account/getCustomAccount',
      getCustomAccountLink: 'account/getCustomAccountLink',
      createCustomAccount: 'account/createCustomAccount',
      deleteExternalAccount: 'account/deleteExternalAccount',
      setExternalAccountAsPrimary: 'account/setExternalAccountAsPrimary',
      addAlert: 'alert/addAlert'
    }),

    ...mapMutations({
      clearExternalAccounts: 'account/clearExternalAccounts'
    }),

    onSubmit() {
      return this.customAccountStatus
        ? this.addBankAccount()
        : this.getStripeLink()
    },

    addBankAccount() {
      this.show = true
    },

    onClickRemove(id) {
      this.current = id
      this.confirmDelete = true
    },

    onModalHidden() {
      this.$refs.payoutForm.reset()
    },

    async onClickDelete() {
      this.loading = true
      await this.deleteExternalAccount(this.current).then(() => {
        this.loading = false
        this.confirmDelete = false
        this.addAlert({
          label: 'Bank account has been removed'
        })
      })
    },

    async setDefaultCard(id) {
      this.loading = true
      await this.setExternalAccountAsPrimary(id).then(() => {
        this.loading = false
        this.addAlert({
          label: 'Your default bank account has been changed'
        })
      })
    },

    async fetchAccount() {
      this.loading = true
      this.clearExternalAccounts()
      await this.getCustomAccount().catch((error) => {
        const data = error.response.data
        if (data.error_code === 'STRIPE_CONNECT_NOT_FOUND') {
          this.setupStripeConnect()
        } else {
          this.errorMessage = data.message
        }
      })
      this.loading = false
    },

    async getStripeLink() {
      this.loading = true
      const formData = {
        return_url: window.location.href,
        type: 'account_onboarding',
        collect: 'eventually_due'
      }
      await this.getCustomAccountLink(formData).then(({ data }) => {
        window.open(data.data.url, '_blank')
      })
      this.loading = false
    },

    async setupStripeConnect() {
      this.loading = true
      await this.createCustomAccount().catch((error) => {
        const data = error.response.data
        if (data.error_code === 'HTTP_BAD_REQUEST') {
          this.errorMessage = data.message
        }
      })
      this.loading = false
    }
  },

  created() {
    this.fetchAccount()
  }
}
</script>
<style scoped lang="scss">
.payout-list /deep/ {
  .edit-btn {
    width: 130px;
    border-radius: 7px;
    border-width: 1px;
  }

  .remove-link {
    position: absolute;
    top: 22.5px;
    right: 15px;

    > a {
      font-size: 0.95rem;
      font-weight: 600;
      text-decoration: none;
    }
  }

  .remove-link--disabled {
    > a {
      color: var(--input-border-color);
      cursor: not-allowed;
    }
  }

  .submit-btn {
    width: 290px;
  }

  .list-group-item {
    border-radius: 3px;
    margin-bottom: 1.4rem;
    border: 1px solid var(--input-border-color);
  }

  .list-group-item--default {
    border: 1px solid var(--brand-green);
  }

  .modal-form {
    .modal-header {
      padding-bottom: 2rem;

      .close {
        margin-right: -25px;
      }
    }

    .modal-content {
      padding-bottom: 5px;
    }
  }
}

@media (max-width: 568px) {
  .payout-list /deep/ {
    .submit-btn {
      width: 100%;
    }
  }
}
</style>
